/**
 * Labstep
 *
 * @module components/Comment/Item
 * @desc Single comment
 */

import classnames from 'classnames';
import CommentActionMenu from 'labstep-web/components/Comment/Action/Menu';
import { CommentBody } from 'labstep-web/components/Comment/Body';
import { CommentThreadContainer as CommentThread } from 'labstep-web/components/Comment/Thread';
import { ProfilePictureImagePopup as ProfilePictureImage } from 'labstep-web/components/Profile/Picture/Image';
import { ICONS } from 'labstep-web/constants/icons';
import { ToggleContainer } from 'labstep-web/containers/Toggle';
import { Card } from 'labstep-web/core/Card/Card';
import { DateSmall } from 'labstep-web/core/DatePreview';
import { Icon } from 'labstep-web/core/Icon';
import { Size } from 'labstep-web/core/Initials/types';
import type React from 'react';
import styles from './styles.module.scss';
import type { IItemContainerProps, IItemProps } from './types';

export const CommentItem: React.FC<IItemProps> = ({
  comment,
  simple,
  readOnly,
  header,
  showPin,
  actions,
  additionalActions,
  showCommentThread = true,
  ...rest
}) => (
  <div
    data-testid="comment-item"
    className={classnames(styles.commentItemContainer, {
      [styles.indent]:
        (showPin && comment.pinned_at) ||
        (header &&
          (!!comment.parent_thread.protocol_step ||
            !!comment.parent_thread.metadata ||
            !!comment.parent_thread.experiment)),
    })}
  >
    <div>
      {showPin && comment.pinned_at && (
        <div className={styles.headerPin}>
          <b>
            <Icon name={ICONS.comment.actions.pin} />
            Pinned Note
          </b>
        </div>
      )}
      {header && header({ comment })}
    </div>
    <div className={styles.itemContainer}>
      {!simple && (
        <div className={styles.itemLeft}>
          <ProfilePictureImage
            entity={comment.author}
            size={Size.mini}
          />
        </div>
      )}
      <Card className={styles.card}>
        {simple && (
          <ProfilePictureImage
            entity={comment.author}
            size={Size.mini}
          />
        )}
        <div className={styles.itemRight}>
          <div className={styles.itemBody}>
            <div className={styles.itemNameDate}>
              <b>{comment.author.name}</b>
              <DateSmall dateString={comment.created_at} />
            </div>
            <CommentBody comment={comment} {...rest} />
          </div>
          <div className={styles.itemActions}>
            {!readOnly && (
              <>
                <CommentActionMenu
                  toggle={rest.toggle}
                  comment={comment}
                  actions={actions}
                />
                {additionalActions}
                {comment.thread && showCommentThread && (
                  <CommentThread comment={comment} />
                )}
              </>
            )}
          </div>
        </div>
      </Card>
    </div>
  </div>
);

CommentItem.defaultProps = {
  readOnly: false,
};

export const CommentItemContainer: React.FC<IItemContainerProps> = (
  props,
) => (
  <ToggleContainer>
    {(containerProps) => (
      <CommentItem {...props} {...containerProps} />
    )}
  </ToggleContainer>
);
